/**
 * Geocoding utility functions for converting zip codes to coordinates
 */

export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress?: string;
}

/**
 * Geocodes a zip code using Google Maps Geocoding API
 * @param zipCode - The zip code to geocode
 * @returns Promise<GeocodeResult | null> - The coordinates or null if failed
 */
export const geocodeZipCode = async (
  zipCode: string
): Promise<GeocodeResult | null> => {
  try {
    // Check if Google Maps API is available
    if (typeof google === 'undefined' || !google.maps) {
      // eslint-disable-next-line no-console
      console.warn('Google Maps API not loaded');
      return null;
    }

    const geocoder = new google.maps.Geocoder();

    return new Promise(resolve => {
      geocoder.geocode({ address: zipCode }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            formattedAddress: results[0].formatted_address,
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Geocoding failed for zip code ${zipCode}:`, status);
          resolve(null);
        }
      });
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error geocoding zip code:', error);
    return null;
  }
};

/**
 * Geocodes a full address using Google Maps Geocoding API
 * @param address - The full address to geocode
 * @returns Promise<GeocodeResult | null> - The coordinates or null if failed
 */
export const geocodeAddress = async (
  address: string
): Promise<GeocodeResult | null> => {
  try {
    // Check if Google Maps API is available
    if (typeof google === 'undefined' || !google.maps) {
      // eslint-disable-next-line no-console
      console.warn('Google Maps API not loaded');
      return null;
    }

    const geocoder = new google.maps.Geocoder();

    return new Promise(resolve => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng(),
            formattedAddress: results[0].formatted_address,
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Geocoding failed for address ${address}:`, status);
          resolve(null);
        }
      });
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error geocoding address:', error);
    return null;
  }
};

/**
 * Validates if a zip code format is valid for US zip codes
 * @param zipCode - The zip code to validate
 * @returns boolean - True if valid format
 */
export const isValidZipCode = (zipCode: string): boolean => {
  const zipCodeRegex = /^\d{5}(-\d{4})?$/;
  return zipCodeRegex.test(zipCode);
};

/**
 * Geocodes a zip code with fallback to default coordinates
 * @param zipCode - The zip code to geocode
 * @param fallbackCoordinates - Default coordinates to use if geocoding fails
 * @returns Promise<GeocodeResult> - Always returns coordinates
 */
export const geocodeZipCodeWithFallback = async (
  zipCode: string,
  fallbackCoordinates: GeocodeResult = {
    lat: 40.66112917128207,
    lng: -73.98596198786989,
  } // Default Brooklyn, NY
): Promise<GeocodeResult> => {
  if (!isValidZipCode(zipCode)) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid zip code format: ${zipCode}`);
    return fallbackCoordinates;
  }

  const result = await geocodeZipCode(zipCode);
  return result || fallbackCoordinates;
};

/** US ZIP for forms/API: 5 digits only (drops ZIP+4 from Google). */
export const normalizeUsZipCodeFiveDigits = (zip: string): string => {
  const trimmed = zip.trim();
  const m = trimmed.match(/^(\d{5})/);
  return m ? m[1] : trimmed;
};

function latLngOrLiteralToLiteral(
  loc: google.maps.LatLng | google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
  if (typeof loc.lat === 'function') {
    return {
      lat: (loc as google.maps.LatLng).lat(),
      lng: (loc as google.maps.LatLng).lng(),
    };
  }
  return loc as google.maps.LatLngLiteral;
}

/**
 * Parses Google Places API address components into structured address data
 * @param addressComponents - Array of address components from Google Places API
 * @returns Parsed address object
 */
export const parseAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[]
): {
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  formattedAddress: string;
} => {
  const result = {
    streetNumber: '',
    streetName: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    formattedAddress: '',
  };

  let postalCode = '';

  addressComponents.forEach(component => {
    const types = component.types;

    if (types.includes('street_number')) {
      result.streetNumber = component.long_name;
    } else if (types.includes('route')) {
      result.streetName = component.long_name;
    } else if (types.includes('locality')) {
      result.city = component.long_name;
    } else if (
      (types.includes('sublocality') || types.includes('neighborhood')) &&
      !result.city
    ) {
      result.city = component.long_name;
    } else if (types.includes('administrative_area_level_1')) {
      result.state = component.short_name;
    } else if (types.includes('postal_code')) {
      postalCode = component.long_name;
    } else if (types.includes('country')) {
      result.country = component.long_name;
    }
  });

  result.zipCode = normalizeUsZipCodeFiveDigits(postalCode);

  // Construct formatted address
  const streetAddress = [result.streetNumber, result.streetName]
    .filter(Boolean)
    .join(' ');

  result.formattedAddress = [
    streetAddress,
    result.city,
    result.state,
    result.zipCode,
    result.country,
  ]
    .filter(Boolean)
    .join(', ');

  return result;
};

/**
 * Reverse-geocodes a point and returns the first postal code found in any result.
 * Use when Place Details omit postal_code (e.g. routes, highways, large POIs).
 */
export const reverseGeocodeFindPostalCode = async (
  location: google.maps.LatLngLiteral
): Promise<string | null> => {
  if (typeof google === 'undefined' || !google.maps) {
    return null;
  }

  const geocoder = new google.maps.Geocoder();

  return new Promise(resolve => {
    geocoder.geocode({ location }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK || !results?.length) {
        resolve(null);
        return;
      }
      for (const geocodeResult of results) {
        const parsed = parseAddressComponents(geocodeResult.address_components);
        if (parsed.zipCode) {
          resolve(parsed.zipCode);
          return;
        }
      }
      resolve(null);
    });
  });
};

/**
 * Resolves US ZIP from a Places result: uses address_components when present,
 * otherwise reverse-geocodes the place geometry.
 */
export const resolvePlacePostalCode = async (
  place: google.maps.places.PlaceResult,
  parsed?: ReturnType<typeof parseAddressComponents>
): Promise<string> => {
  const p = parsed ?? parseAddressComponents(place.address_components ?? []);
  if (p.zipCode) {
    return p.zipCode;
  }
  const loc = place.geometry?.location;
  if (!loc) {
    return '';
  }
  const { lat, lng } = latLngOrLiteralToLiteral(loc);
  return (await reverseGeocodeFindPostalCode({ lat, lng })) || '';
};

/**
 * Geocodes an address and returns detailed address components
 * @param address - The address to geocode
 * @returns Promise with detailed address information or null if failed
 */
export const geocodeAddressWithDetails = async (
  address: string
): Promise<{
  lat: number;
  lng: number;
  formattedAddress: string;
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
} | null> => {
  try {
    // Check if Google Maps API is available
    if (typeof google === 'undefined' || !google.maps) {
      // eslint-disable-next-line no-console
      console.warn('Google Maps API not loaded');
      return null;
    }

    const geocoder = new google.maps.Geocoder();

    return new Promise(resolve => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          const addressComponents = parseAddressComponents(
            results[0].address_components
          );

          resolve({
            lat: location.lat(),
            lng: location.lng(),
            ...addressComponents,
            formattedAddress: results[0].formatted_address,
          });
        } else {
          // eslint-disable-next-line no-console
          console.warn(`Geocoding failed for address ${address}:`, status);
          resolve(null);
        }
      });
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error geocoding address with details:', error);
    return null;
  }
};
